import type { JSX } from 'react';
import type { UpdateUseCaseBody } from '@majuscule/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from './toast';
import { SheetSection } from './use-cases/shared';
import { internalSend, validationDetail } from '../lib/api';
import type { UseCase } from '../lib/use-cases';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { KvList, KvRow } from '@/components/ui/kv';
import { Textarea } from '@/components/ui/textarea';

/** Qonto line title limit (domain `INVOICE_NAME_MAX`). */
const NAME_MAX = 40;
/** Domain `INVOICE_DESCRIPTION_MAX`. */
const DESCRIPTION_MAX = 300;

/**
 * How the use case reads on the client's invoice (lot 18): the title of its
 * lines (its key when empty) and the text printed under them. A section of the
 * use-case sheet, edited in a dialog.
 */
export function UseCaseInvoiceCard({ useCase }: { useCase: UseCase }): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState('');

  const openEdit = (): void => {
    setName(useCase.invoiceName ?? '');
    setDescription(useCase.invoiceDescription ?? '');
    setFormError('');
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (body: UpdateUseCaseBody) =>
      internalSend<UseCase>('PATCH', `/internal/use-cases/${useCase.id}`, body),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['use-cases'] });
      notify('Libellés de facture enregistrés');
    },
    onError: (error) => {
      setFormError(validationDetail(error) ?? 'Échec de l’enregistrement des libellés de facture.');
    },
  });

  const submit = (): void => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    save.mutate({
      invoiceName: trimmedName === '' ? null : trimmedName,
      invoiceDescription: trimmedDescription === '' ? null : trimmedDescription,
    });
  };

  return (
    <SheetSection
      title="Sur la facture"
      description="Titre de la ligne de ce cas d’usage sur la facture (tous utilisateurs confondus) et texte imprimé dessous sur le PDF. Seul le titre est transmis en facture électronique."
      actions={
        <Button type="button" size="sm" variant="outline" onClick={openEdit}>
          Modifier
        </Button>
      }
    >
      <KvList>
        <KvRow label="Nom sur la facture">
          {useCase.invoiceName ?? (
            <span className="text-muted-foreground">
              <span className="font-mono">{useCase.key}</span> (clé, par défaut)
            </span>
          )}
        </KvRow>
        <KvRow label="Description sur la facture">
          <span className="whitespace-pre-line">{useCase.invoiceDescription ?? '—'}</span>
        </KvRow>
      </KvList>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title="Libellés de facture"
            description={
              <>
                Cas d’usage <span className="font-mono">{useCase.key}</span>. S’applique aux
                prochaines factures.
              </>
            }
          />
          <DialogBody>
            <Field
              label="Nom sur la facture"
              hint={`${name.length}/${NAME_MAX} caractères. Vide : la clé du cas d’usage.`}
            >
              <Input
                value={name}
                maxLength={NAME_MAX}
                placeholder="Extraction de factures fournisseurs"
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
            <Field
              label="Description"
              hint={`${description.length}/${DESCRIPTION_MAX} caractères.`}
            >
              <Textarea
                value={description}
                maxLength={DESCRIPTION_MAX}
                rows={3}
                placeholder="Lecture automatique de vos factures fournisseurs et saisie dans votre ERP."
                onChange={(event) => {
                  setDescription(event.target.value);
                }}
              />
            </Field>
            {formError.length > 0 ? <FieldError>{formError}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button type="button" size="lg" onClick={submit} disabled={save.isPending}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SheetSection>
  );
}
